/* ---------------------------------------------------------------------------
   How a structure's condition has moved over its inspections (LC-62): the
   whole structure's rating, and each element's, visit by visit.

   ONE SCALE. A rating is carried at the scale its source used and never
   rescaled (see the Inspection history table), so a 4-of-5 is never drawn
   beside a 6-of-9: the trend is drawn on the scale of the latest inspection,
   and visits on another scale are counted, not plotted.

   ONLY WHAT IS ON RECORD. A draft, an entry in review and one still on the
   device are not evidence of a condition yet (LC-61), so they are left out, as
   the cycle leaves them out. An element marked `N` — not accessible — is a
   gap in its line, not a zero.
   --------------------------------------------------------------------------- */

import { elementLabel } from './elements.ts';
import type { Inspection } from './types.ts';

export interface TrendPoint {
  date: string;
  value: number;
  inspectionId: string | null;
}

export interface TrendSeries {
  /** `structure` for the whole, else the element id. */
  key: string;
  label: string;
  points: TrendPoint[];
}

export interface ConditionTrend {
  scale: string;
  /** The whole structure first, then each element in the order first assessed. */
  series: TrendSeries[];
  /** Visits on file on another scale, not drawn. */
  otherScale: number;
}

/** Whether an inspection is on record: accepted, or on file from before the workflow. */
function onRecord(i: Inspection): boolean {
  return !i.pending && (i.state === undefined || i.state === 'accepted');
}

export function conditionTrend(inspections: readonly Inspection[]): ConditionTrend | null {
  const kept = inspections.filter(onRecord).slice().sort((a, b) => a.date.localeCompare(b.date));
  if (!kept.length) return null;
  const scale = kept[kept.length - 1]!.scale;
  const same = kept.filter((i) => i.scale === scale);

  const structure: TrendSeries = {
    key: 'structure',
    label: 'Whole structure',
    points: same.map((i) => ({ date: i.date, value: i.rating, inspectionId: i.id ?? null })),
  };
  const byElement = new Map<string, TrendSeries>();
  for (const i of same) {
    for (const e of i.elements ?? []) {
      if (typeof e.rating !== 'number') continue;
      const series = byElement.get(e.element) ?? { key: e.element, label: elementLabel(e.element), points: [] };
      series.points.push({ date: i.date, value: e.rating, inspectionId: i.id ?? null });
      byElement.set(e.element, series);
    }
  }
  return { scale, series: [structure, ...byElement.values()], otherScale: kept.length - same.length };
}
